import { BaseDialog, Button, ConfirmDialog, HookedForm, InputField, IntegrationMenu } from '@app/components';
import { ContactUsEmail, IntegrationLabels, IntegrationType } from '@app/utils/consts';
import { useIntegrationsReviews } from './IntegrationsReviews.hooks';
// import { useAppSelector } from '@app/redux/hooks';
import BulbIcon from '@app/assets/icons/menu/bulb.svg?react';
import AddIcon from '@app/assets/icons/add.svg?react';
import CloseIcon from '@app/assets/icons/close.svg?react';
import GoogleLogo from '@app/assets/icons/Google.svg?react';

export const IntegrationsReviews = () => {
  const {
    saveIntegrationForm,
    saveIntegrationSubmit,
    handleIntegrationModal,
    integrationFormModal,
    listOfIntegrations,
    handleUpdateIntegrationModal,
    updateIntegrationFormModal,
    updateIntegrationForm,
    updateIntegrationSubmit,
    handleGoogleIntegration,
    deleteIntegrationAction,
    confirmDeleteIntegration,
    confirmDeleteType,
    setConfirmDeleteType,
    isConnectingGoogle,
  } = useIntegrationsReviews();

  // const user = useAppSelector((state) => state.auth?.user);

  // Reorder integrations to put Google first
  const sortedIntegrations = [...listOfIntegrations].sort((a, b) => {
    if (a.type === IntegrationType.GOOGLE) return -1;
    if (b.type === IntegrationType.GOOGLE) return 1;
    return 0;
  });

  return (
    <>
      <div>
        <div className="bg-white border-b border-b-base-ghostWhite ">
          <div className="container mx-auto p-4 flex flex-col md:flex-row justify-between gap-4">
            <div>
              <h2 className="font-bold text-[28px]">Review Platforms</h2>
              <p className="text-base-coolGray">
                Choose the platforms you want to use for gathering reviews. Integrating with these platforms
                <br /> will enable you to send requests and respond to reviews on this service.
              </p>
            </div>
            <div>
              <div className="bg-brand-lightBlue3 p-4  rounded-lg text-sm font-medium flex gap-2">
                <div>
                  <BulbIcon className="[&_path]:stroke-brand-baseBlue" width={20} height={20} />
                </div>
                <div>
                  <p>
                    Want to integrate a platform that is not listed
                    <br /> here?{' '}
                    <a href={`mailto:${ContactUsEmail}`} className="text-[#3279C5] font-bold">
                      Contact us!
                    </a>
                    <br /> And we will consider the possibility of adding it
                    <br /> to the list of integrations.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="container mx-auto p-4">
          <div className={sortedIntegrations.length === 1 ? 'flex items-center justify-center' : 'grid grid-cols-1 md:grid-cols-3 xl:grid-cols-4 gap-4'}>
            {sortedIntegrations.map((integration) => {
              return (
                <div key={integration.type} className="bg-white p-4 rounded-lg flex flex-col h-full" style={sortedIntegrations.length === 1 ? { maxWidth: '400px' } : {}}>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 rounded-lg bg-base-antiFlash flex items-center justify-center">{integration.Icon && <integration.Icon />}</div>
                      <div className="flex items-center gap-2">
                        <p className="text-base-davyGray font-medium">
                          {integration.type === IntegrationType.GOOGLE
                            ? 'Google'
                            : integration.type === IntegrationType.YELP
                              ? 'Yelp'
                              : integration.title}
                        </p>
                        {integration.type === IntegrationType.GOOGLE && (
                          <span className="bg-brand-baseBlue text-white text-xs px-2 py-1 rounded-full font-medium">
                            Default
                          </span>
                        )}
                      </div>
                    </div>
                    {integration.hasConnection && (
                      <IntegrationMenu
                        id={integration.type}
                        updateAction={() => handleUpdateIntegrationModal(integration)}
                        deleteAction={() => deleteIntegrationAction(integration.type)}
                        disabled={isConnectingGoogle}
                      />
                    )}
                  </div>
                  <p className="text-base-davyGray text-sm flex-grow mb-4">
                    {integration?.email && integration.hasConnection && (
                      <>
                        <span className="font-medium text-brand-baseBlue">
                          {integration?.email}
                        </span>{' '}
                        is linked to your Echo business account.
                        <br />
                        <br />
                      </>
                    )}
                    {integration.type === IntegrationType.YELP ? (
                      <>
                        To connect your Yelp business, you'll need your Yelp
                        Business ID. You can find this in your Yelp Business
                        Account URL (e.g., yelp.com/biz/your-business-id). This
                        integration will allow you to view and manage your Yelp
                        reviews directly in our platform.
                      </>
                    ) : integration.type === IntegrationType.GOOGLE ? (
                      <>
                        To connect your Google Business Profile, you'll need to
                        authenticate with your Google account first. Click the
                        Connect button to enable the integration, then you'll be
                        able to view and manage your Google reviews directly in
                        our platform. Make sure you have access to your Google
                        Business Profile before connecting.
                      </>
                    ) : integration.type === IntegrationType.TRUSTPILOT ? (
                      <>
                        Connect your Trustpilot business account to manage and
                        respond to customer reviews from one centralized
                        location. This integration will sync your Trustpilot
                        reviews and allow you to monitor your business
                        reputation in real-time.
                      </>
                    ) : integration.type === IntegrationType.FACEBOOK ? (
                      <>
                        Integrate with Facebook to manage your business page
                        reviews and ratings. This will allow you to view,
                        respond to, and monitor customer feedback from your
                        Facebook business page directly within our platform.
                      </>
                    ) : (
                      'Connect your business account to manage and respond to customer reviews from this platform directly within our system.'
                    )}
                  </p>
                  <div className="flex justify-end mt-auto">
                    {integration.type === IntegrationType.GOOGLE &&
                    !integration.available ? (
                      <Button
                        onClick={() => handleGoogleIntegration(integration)}
                        leftNode={<GoogleLogo className="w-5 h-5" />}
                        className="min-w-fit"
                        disabled={isConnectingGoogle}
                      >
                        {/*Log in with Google*/}
                        Connect Google Account
                      </Button>
                    ) : (
                      <Button
                        disabled={!integration.available || isConnectingGoogle}
                        leftNode={integration.available ? <AddIcon /> : undefined}
                        onClick={() => {
                          if (integration.hasConnection) {
                            handleUpdateIntegrationModal(integration);
                            // } else if (integration.type === IntegrationType.GOOGLE) {
                            //   handleGoogleIntegration(integration);      // This case is handled before (integration.type === IntegrationType.GOOGLE && !integration.available)
                          } else {
                            handleIntegrationModal(integration.type);
                          }
                        }}
                        className={`min-w-fit ${!integration.available ? '!bg-[#E7EAF4] !text-black' : ''}`}
                      >
                        {integration.hasConnection
                          ? 'Edit Connection'
                          : integration.available
                            ? 'Connect'
                            : 'Coming soon'}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="my-16">
            <div className="max-w-xl mx-auto border-dashed border-2 border-base-lavender p-6 space-y-6 text-center rounded-xl">
              <h5 className="font-bold">Don't see your review platform?</h5>
              <p className="text-base-davyGray">Click here and request that it be added</p>
              <a href={`mailto:${ContactUsEmail}`} className="inline-block">
                <Button className="mx-auto">Contact us</Button>
              </a>
            </div>
          </div>
        </div>
      </div>
      <BaseDialog
        open={Boolean(updateIntegrationFormModal)}
        onClose={() => handleUpdateIntegrationModal()}
      >
        <div className="border-b border-b-base-ghostWhite p-6 mb-6 flex gap-2 items-start justify-between">
          <div className="bg-base-antiFlash flex items-center p-2 rounded-lg">
            <AddIcon
              width={24}
              height={24}
              className="[&_path]:stroke-base-davyGray"
            />
          </div>
          <div className="mr-auto">
            <h5 className="font-bold text-lg leading-tight">
              Update integration information
            </h5>
            <p className="text-base-coolGray text-sm">
              Update the information to connect to the platform.
            </p>
          </div>
          <div>
            <button
              type="button"
              onClick={() => handleUpdateIntegrationModal()}
            >
              <CloseIcon />
            </button>
          </div>
        </div>
        <div className="px-6 pb-4 space-y-4">
          <p className="text-base-davyGray text-sm">
            {updateIntegrationFormModal === IntegrationType.YELP ? (
              <>
                To update your Yelp business connection, enter your Yelp Business ID. You can find this in your Yelp Business Account URL (e.g., yelp.com/biz/your-business-id).
              </>
            ) : updateIntegrationFormModal === IntegrationType.GOOGLE ? (
              <>
                Update your Google review request URL to ensure proper integration with your Google Business Profile. You can find your review request URL in your Google Business Profile settings.
              </>
            ) : (
              'Some Platforms require some information so that we can connect with them, this can be an API Key or Business ID (or business alias)'
            )}
          </p>
          <HookedForm
            formHook={updateIntegrationForm}
            onSubmit={updateIntegrationSubmit}
            id="updateIntegration"
          >
            <input
              {...updateIntegrationForm.register('type')}
              type="hidden"
              name="type"
              value={updateIntegrationFormModal}
            />
            <InputField
              name="connectionId"
              placeholder={
                updateIntegrationFormModal === IntegrationType.YELP
                  ? "Enter your Yelp Business ID (e.g., 'your-business-id')"
                  : updateIntegrationFormModal === IntegrationType.GOOGLE
                  ? "Enter your Google review request URL (e.g., 'https://g.page/r/your-business/review')"
                  : "Enter your API Key or Business ID here"
              }
              defaultValue={updateIntegrationForm.getValues('connectionId')}
            />
          </HookedForm>
        </div>
        <div className="flex gap-4 pb-6 px-6">
          <Button kind="secondary" className="w-full" onClick={() => handleUpdateIntegrationModal()}>
            Cancel
          </Button>
          <Button form="updateIntegration" className="w-full" type="submit">
            Save
          </Button>
        </div>
      </BaseDialog>
      <BaseDialog
        open={Boolean(integrationFormModal)}
        onClose={() => handleIntegrationModal()}
      >
        <div className="border-b border-b-base-ghostWhite p-6 mb-6 flex gap-2 items-start justify-between">
          <div className="bg-base-antiFlash flex items-center p-2 rounded-lg">
            <AddIcon
              width={24}
              height={24}
              className="[&_path]:stroke-base-davyGray"
            />
          </div>
          <div className="mr-auto">
            <h5 className="font-bold text-lg leading-tight">
              Add integration information
            </h5>
            <p className="text-base-coolGray text-sm">
              Add required information to connect to the platform.
            </p>
          </div>
          <div>
            <button type="button" onClick={() => handleIntegrationModal()}>
              <CloseIcon />
            </button>
          </div>
        </div>
        <div className="px-6 pb-4 space-y-4">
          <p className="text-base-davyGray text-sm">
            {integrationFormModal === IntegrationType.GOOGLE ? (
              <>
                To connect your Google Business Profile, you'll need to authenticate with your Google account and provide your review request URL. This will allow you to view and manage your Google reviews directly in our platform. You can find your review request URL in your Google Business Profile settings.
              </>
            ) : (
              <>
                Some Platforms require some information so that we can connect with
                them, this can be an API Key or Business ID (or business alias)
              </>
            )}
          </p>
          <HookedForm
            formHook={saveIntegrationForm}
            onSubmit={saveIntegrationSubmit}
            id="saveIntegration"
          >
            <input
              {...saveIntegrationForm.register('type')}
              type="hidden"
              name="type"
              value={integrationFormModal}
            />
            <InputField
              name="connectionId"
              placeholder={
                integrationFormModal === IntegrationType.YELP
                  ? "Enter your Yelp Business ID (e.g., 'your-business-id')"
                  : integrationFormModal === IntegrationType.GOOGLE
                  ? "Enter your Google review request URL (e.g., 'https://g.page/r/your-business/review')"
                  : "Enter you API Key or Business ID here"
              }
            />
          </HookedForm>
        </div>
        <div className="flex gap-4 pb-6 px-6">
          <Button
            kind="secondary"
            className="w-full"
            onClick={() => handleIntegrationModal()}
          >
            Cancel
          </Button>
          <Button form="saveIntegration" className="w-full" type="submit">
            Save
          </Button>
        </div>
      </BaseDialog>
      <ConfirmDialog
        open={Boolean(confirmDeleteType)}
        onClose={() => setConfirmDeleteType(undefined)}
        onConfirm={confirmDeleteIntegration}
        title={`Disconnect ${confirmDeleteType ? IntegrationLabels[confirmDeleteType] : ''}  Integration?`}
        message={`All reviews imported from ${confirmDeleteType ? IntegrationLabels[confirmDeleteType] : 'integration'} will be removed from the system. If you reconnect the integration later, your reviews will be synced again automatically.`}
        confirmText="Disconnect"
        variant="danger"
      />
    </>
  );
};
